/**
 * ABUSOS E BLOQUEIO DE IP.
 *
 * Pedido do Jhou em 31/10/2026: *"cria um item no menu onde essa regra vai
 * estar escrita, onde eu posso mexer, editar ela. E caso tenha algum cliente
 * que esteja bloqueado, eu também possa fazer esse desbloqueio via painel"*.
 *
 * A TELA MOSTRA O VEREDITO, NÃO O NÚMERO CRU, e essa é a decisão de desenho
 * principal. Medido em produção antes de desenhar:
 *
 *     45.163.1.130    599 falhas · 118 logins OK ·  1 conta  -> CLIENTE
 *     152.245.126.215  61 falhas · 342 logins OK ·  7 contas -> ESCRITÓRIO
 *     64.227.117.136   20 falhas ·   0 logins OK · 16 contas -> varredura
 *
 * O maior gerador de falhas da plataforma é um cliente com senha velha salva.
 * Uma tabela ordenada por "falhas" sem dizer isso convidaria o operador a
 * bloquear o cliente primeiro — e ele não teria como saber. Por isso a coluna
 * "o que é" vem antes dos números, e o botão de bloquear de um `cliente` pede
 * confirmação dizendo o que vai acontecer.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { seguranca, type AbusoIP, type RegraBloqueio } from "../api/admin";
import { explicar } from "../api/problema";
import { Icone } from "../ui/Icone";
import { dataCurta } from "../ui/formato";
import {
  Botao,
  Cabecalho,
  Celula,
  Estado,
  Linha,
  Paginacao,
  Selo,
  Tabela,
  usePagina,
} from "./comuns";

/** O veredito, com a cor que diz o que fazer. */
const VEREDITOS: Record<string, { rotulo: string; tom: "ok" | "aviso" | "erro" | "neutro" }> = {
  cliente: { rotulo: "cliente", tom: "ok" },
  escritorio: { rotulo: "escritório", tom: "ok" },
  varredura: { rotulo: "varredura de contas", tom: "erro" },
  "forca-bruta": { rotulo: "força bruta", tom: "erro" },
  suspeito: { rotulo: "suspeito", tom: "aviso" },
  ruido: { rotulo: "ruído", tom: "neutro" },
};

export function Seguranca() {
  const fila = useQueryClient();
  const [soSuspeitos, setSoSuspeitos] = useState(true);
  const [pagina, setPagina] = usePagina(String(soSuspeitos));
  const [erro, setErro] = useState("");
  const [recado, setRecado] = useState("");

  const lista = useQuery({
    queryKey: ["p", "abusos", soSuspeitos, pagina],
    queryFn: () =>
      seguranca.abusos({
        so_suspeitos: soSuspeitos || undefined,
        pagina,
        por_pagina: 50,
      }),
    refetchInterval: 60_000,
  });

  const recarregar = () => {
    void fila.invalidateQueries({ queryKey: ["p", "abusos"] });
    void fila.invalidateQueries({ queryKey: ["p", "regra-bloqueio"] });
  };

  const bloquear = useMutation({
    mutationFn: (v: { ip: string; motivo: string; horas: number }) =>
      seguranca.bloquear(v.ip, v.motivo, v.horas),
    onSuccess: (r) => {
      setErro("");
      setRecado(`${r.ip} bloqueado. ${r.alcance}`);
      recarregar();
    },
    onError: (e) => setErro(explicar(e)),
  });

  const desbloquear = useMutation({
    mutationFn: (uuid: string) => seguranca.desbloquear(uuid),
    onSuccess: (r) => {
      setErro("");
      // O QUE ACONTECEU É DITO, porque desbloquear automático faz UMA COISA A
      // MAIS: marca a exceção. Sem avisar, o operador não saberia que aquele
      // IP passou a ser ignorado pela regra para sempre.
      setRecado(
        r.excecao_marcada
          ? `${r.ip} desbloqueado, e a regra automática passou a ignorá-lo. ` +
            `Sem isso ela o bloquearia de novo na próxima falha.`
          : `${r.ip} desbloqueado.`,
      );
      recarregar();
    },
    onError: (e) => setErro(explicar(e)),
  });

  return (
    <>
      <Cabecalho
        titulo="Abusos e bloqueio de IP"
        descricao="Quem tentou senha nas portas de e-mail, e o que fazer a respeito."
      />

      <ARegra />

      {recado && (
        <p
          role="status"
          className="mt-4 flex items-start gap-2 rounded-xl border border-acao/40 bg-acao-clara px-4 py-3 text-sm text-acao-escura"
        >
          <Icone nome="Info" tamanho={16} className="mt-0.5 flex-shrink-0" />
          <span>{recado}</span>
        </p>
      )}
      {erro && <p className="mt-4 text-sm text-perigo">{erro}</p>}

      <div className="mt-6 mb-3 flex flex-wrap items-center gap-2">
        {/* O PADRÃO É "SÓ OS QUE MERECEM AÇÃO", porque a lista inteira tem
            centenas de IPs e a maioria é sonda que tenta duas vezes e vai
            embora. Abrir com tudo faria o que importa se perder no meio. */}
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            onClick={() => setSoSuspeitos(v)}
            className={`rounded-full px-3 py-1 text-xs ${
              soSuspeitos === v
                ? "bg-acao text-white"
                : "border border-linha text-tinta-media"
            }`}
          >
            {v ? "Só o que merece ação" : "Todos os IPs"}
          </button>
        ))}
      </div>

      <Estado consulta={lista} vazio={lista.data?.itens?.length === 0}>
        <Tabela
          fixa
          colunas={["IP", "O que é", "Falhas", "Logins OK", "Contas", "Visto", ""]}
          larguras={["w-36", "w-44", "w-20", "w-24", "w-auto", "w-24", "w-48"]}
        >
          {lista.data?.itens?.map((a) => (
            <LinhaDoIP
              key={a.ip}
              abuso={a}
              aoBloquear={(motivo, horas) =>
                bloquear.mutate({ ip: a.ip, motivo, horas })
              }
              aoDesbloquear={() =>
                a.bloqueio && desbloquear.mutate(a.bloqueio.uuid)
              }
              ocupado={bloquear.isPending || desbloquear.isPending}
            />
          ))}
        </Tabela>
      </Estado>
      <Paginacao pagina={pagina} dados={lista.data} aoMudar={setPagina} nome="IPs" />

      {/* O QUE O BLOQUEIO FAZ, E O QUE ELE NÃO DIZ.
          Desde a migração 57 ele vale nas seis portas: envio pelo mapa que o
          Postfix lê, leitura por uma passdb `deny` do Dovecot, as duas sobre
          a mesma tabela. O que continua valendo avisar é a DIFERENÇA de
          diagnóstico entre as duas — no envio o cliente recebe o motivo, na
          leitura ele vê "senha errada". Quem bloqueia por engano precisa
          saber disso para reconhecer o sintoma depois. */}
      <p className="mt-6 rounded-xl border border-linha bg-fundo px-4 py-3 text-xs text-tinta-media">
        <strong className="text-tinta">O alcance do bloqueio:</strong> ele barra{" "}
        <strong>envio</strong> (587 e 465) <strong>e leitura</strong> (IMAP,
        POP3 e ManageSieve), e vale no instante em que você clica — sem
        reiniciar nada.{" "}
        <strong>A diferença está no que o cliente vê:</strong> no envio ele
        recebe o motivo que você escreveu; na leitura, o programa de e-mail dele
        mostra apenas <em>senha incorreta</em>, porque o Dovecot não tem como
        explicar. Se alguém reclamar de "senha errada" sem motivo, procure o
        IP nesta lista antes de redefinir a senha dele.
      </p>
    </>
  );
}

/** A regra automática, editável. */
function ARegra() {
  const fila = useQueryClient();
  const [erro, setErro] = useState("");
  const consulta = useQuery({
    queryKey: ["p", "regra-bloqueio"],
    queryFn: () => seguranca.regra(),
  });
  const [campos, setCampos] = useState<Partial<RegraBloqueio>>({});

  const salvar = useMutation({
    mutationFn: (dados: Partial<RegraBloqueio>) => seguranca.salvarRegra(dados),
    onSuccess: (r) => {
      setErro("");
      setCampos({});
      fila.setQueryData(["p", "regra-bloqueio"], r);
      void fila.invalidateQueries({ queryKey: ["p", "abusos"] });
    },
    onError: (e) => setErro(explicar(e)),
  });

  const r = consulta.data;
  if (!r) return null;
  const v = { ...r, ...campos };
  const mudou = Object.keys(campos).length > 0;

  return (
    <section
      className={`rounded-xl border p-5 ${
        v.ativa ? "border-acao/40 bg-acao-clara/30" : "border-linha bg-white"
      }`}
    >
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-medium text-tinta">
          <Icone nome="ShieldCheck" tamanho={16} className="text-acao" />
          Regra automática
        </h2>
        <Selo
          texto={v.ativa ? "ligada" : "desligada"}
          tom={v.ativa ? "ok" : "neutro"}
        />
        <span className="ml-auto text-xs text-tinta-fraca">
          alterada por {r.alterado_por} · {dataCurta(r.alterado_em)}
        </span>
      </div>

      {/* A REGRA ESCRITA EM PORTUGUÊS, montada dos valores em vigor.
          Três caixinhas de número não dizem o que a regra faz; a frase diz.
          E ela acompanha o que está sendo editado, antes de salvar. */}
      <p className="mt-3 text-sm text-tinta">
        Bloquear por <strong>{v.horas_bloqueio}h</strong> o IP que tentar{" "}
        <strong>{v.min_contas} contas diferentes</strong> ou acumular{" "}
        <strong>{v.min_falhas} falhas</strong> — e que{" "}
        <strong>nunca tenha entrado</strong>.
      </p>

      <div className="mt-4 flex flex-wrap items-end gap-3">
        <label className="text-xs text-tinta-fraca">
          Contas diferentes
          <input
            type="number"
            min={2}
            max={1000}
            value={v.min_contas}
            onChange={(e) =>
              setCampos((c) => ({ ...c, min_contas: Number(e.target.value) }))
            }
            className="mt-1 block w-28 rounded-lg border border-linha px-2 py-1.5 text-sm text-tinta"
          />
        </label>
        <label className="text-xs text-tinta-fraca">
          Ou falhas
          <input
            type="number"
            min={10}
            max={100000}
            value={v.min_falhas}
            onChange={(e) =>
              setCampos((c) => ({ ...c, min_falhas: Number(e.target.value) }))
            }
            className="mt-1 block w-28 rounded-lg border border-linha px-2 py-1.5 text-sm text-tinta"
          />
        </label>
        <label className="text-xs text-tinta-fraca">
          Horas de bloqueio
          <input
            type="number"
            min={1}
            max={8760}
            value={v.horas_bloqueio}
            onChange={(e) =>
              setCampos((c) => ({ ...c, horas_bloqueio: Number(e.target.value) }))
            }
            className="mt-1 block w-28 rounded-lg border border-linha px-2 py-1.5 text-sm text-tinta"
          />
        </label>

        <Botao
          aoClicar={() => salvar.mutate({ ...campos, ativa: v.ativa })}
          ocupado={salvar.isPending}
          desabilitado={!mudou}
          icone="Check"
        >
          Salvar a regra
        </Botao>
        <Botao
          tom={v.ativa ? "perigo" : "primario"}
          aoClicar={() => salvar.mutate({ ativa: !v.ativa })}
          ocupado={salvar.isPending}
        >
          {v.ativa ? "Desligar a regra" : "Ligar a regra"}
        </Botao>
      </div>

      {mudou && (
        <p className="mt-2 text-xs text-aviso">
          Alterado e ainda não salvo.
        </p>
      )}
      {erro && <p className="mt-2 text-sm text-perigo">{erro}</p>}

      {/* A TRAVA, mostrada e não escondida.
          Ela não é editável, e uma tela que não a dissesse deixaria o
          operador achando que a regra pode banir qualquer um — ou procurando
          onde desligá-la. O texto vem do servidor, para não divergir dele. */}
      <p className="mt-4 flex items-start gap-2 rounded-lg border border-linha bg-white px-3 py-2 text-xs text-tinta-media">
        <Icone nome="Lock" tamanho={13} className="mt-0.5 flex-shrink-0 text-tinta-fraca" />
        <span>{r.trava}</span>
      </p>
    </section>
  );
}

function LinhaDoIP({
  abuso,
  aoBloquear,
  aoDesbloquear,
  ocupado,
}: {
  abuso: AbusoIP;
  aoBloquear: (motivo: string, horas: number) => void;
  aoDesbloquear: () => void;
  ocupado: boolean;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const v = VEREDITOS[abuso.veredito] ?? { rotulo: abuso.veredito, tom: "neutro" as const };
  // Bloquear quem JÁ ENTROU é quase sempre engano, e a tela não pode deixar
  // isso passar num clique: `sucessos > 0` significa que existe gente usando
  // aquele endereço para trabalhar.
  const ehCliente = abuso.sucessos > 0;

  return (
    <>
      <Linha>
        <Celula>
          <span className="block truncate font-mono text-xs text-tinta" title={abuso.ip}>
            {abuso.ip}
          </span>
        </Celula>
        <Celula>
          <span className="flex flex-wrap items-center gap-1">
            <Selo texto={v.rotulo} tom={v.tom} />
            {abuso.nunca_bloquear && <Selo texto="exceção" tom="neutro" />}
          </span>
        </Celula>
        <Celula>
          <span className="tabular-nums">{abuso.falhas.toLocaleString("pt-BR")}</span>
        </Celula>
        <Celula>
          {/* LOGINS OK EM DESTAQUE quando existem: é o número que diz
              "não bloqueie". */}
          <span
            className={`tabular-nums ${abuso.sucessos > 0 ? "font-semibold text-acao" : "text-tinta-fraca"}`}
          >
            {abuso.sucessos.toLocaleString("pt-BR")}
          </span>
        </Celula>
        <Celula>
          {/* "50+" NO TETO, e não o número cru.
              A contagem só é distinta enquanto o conjunto de contas cabe no
              array do banco, que para em 50 — então acima disso o número
              diria mais do que se pode sustentar. A magnitude continua na
              coluna de falhas, que é exata. */}
          <span
            className="block truncate text-xs text-tinta-fraca"
            title={abuso.contas.join(", ")}
          >
            {abuso.contas_tentadas === 0
              ? "—"
              : `${abuso.contas_tentadas >= 50 ? "50+" : abuso.contas_tentadas} · ${abuso.contas.join(", ")}`}
          </span>
        </Celula>
        <Celula>
          <span className="text-xs text-tinta-fraca">{dataCurta(abuso.ultima_em)}</span>
        </Celula>
        <Celula>
          <span className="flex flex-wrap justify-end gap-1.5">
            {abuso.bloqueado ? (
              <>
                <Selo
                  texto={abuso.bloqueio?.automatico ? "bloqueado pela regra" : "bloqueado"}
                  tom="erro"
                />
                <button
                  type="button"
                  onClick={aoDesbloquear}
                  disabled={ocupado}
                  className="min-h-[30px] whitespace-nowrap rounded-full border border-linha px-2.5 py-1 text-xs text-acao hover:bg-acao-clara disabled:opacity-50"
                >
                  desbloquear
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() =>
                  ehCliente ? setConfirmando(true) : aoBloquear("", 24)
                }
                disabled={ocupado}
                className="min-h-[30px] whitespace-nowrap rounded-full border border-perigo/30 px-2.5 py-1 text-xs text-perigo hover:bg-perigo/10 disabled:opacity-50"
              >
                bloquear 24h
              </button>
            )}
          </span>
        </Celula>
      </Linha>

      {/* A linha de explicação, sempre. É o que transforma o número em
          decisão — e é de graça, porque o servidor já manda a frase. */}
      <tr className="border-b border-linha-suave">
        <td colSpan={7} className="pb-2 pr-3 text-xs text-tinta-fraca">
          {abuso.explicacao}
          {abuso.bloqueado && abuso.bloqueio && (
            <>
              {" "}
              <span className="text-tinta-media">
                Bloqueado por {abuso.bloqueio.bloqueado_por}
                {abuso.bloqueio.expira_em
                  ? ` até ${dataCurta(abuso.bloqueio.expira_em)}`
                  : " sem prazo"}
                : {abuso.bloqueio.motivo}
              </span>
            </>
          )}
        </td>
      </tr>

      {/* CONFIRMAÇÃO SÓ PARA QUEM JÁ ENTROU.
          Pedir confirmação em todo bloqueio treinaria a pessoa a clicar sem
          ler — e aí o aviso não serviria justamente no caso que importa. */}
      {confirmando && (
        <tr>
          <td colSpan={7} className="bg-perigo/5 px-3 py-3">
            <p className="flex items-start gap-2 text-sm text-tinta">
              <Icone nome="AlertTriangle" tamanho={16} className="mt-0.5 flex-shrink-0 text-perigo" />
              <span>
                <strong>Este IP já autenticou {abuso.sucessos} vez(es) aqui.</strong>{" "}
                Quase sempre é um cliente com senha velha salva em algum
                programa — e pode ser a saída de internet de um escritório
                inteiro. Bloquear impede <strong>todas</strong> as pessoas
                desse endereço de enviar e-mail.
                {abuso.contas.length > 0 && (
                  <> Contas vistas: <strong>{abuso.contas.join(", ")}</strong>.</>
                )}
              </span>
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  aoBloquear("bloqueado pelo administrador", 24);
                  setConfirmando(false);
                }}
                className="rounded-full bg-perigo px-3 py-1.5 text-xs text-white"
              >
                Bloquear mesmo assim
              </button>
              <button
                type="button"
                onClick={() => setConfirmando(false)}
                className="rounded-full border border-linha px-3 py-1.5 text-xs text-tinta"
              >
                Cancelar
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
